import { generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { CONSENT_DECISION } from '@/constants/consents';
import { Icon, type IconName } from '@/components/common/Icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RelatedRecordsSection } from '@/components/practitioner/shared/RelatedRecordsSection';
import { usePatientTimelineQuery } from '@/hooks/usePatientsQueries';
import {
  buildAppointmentStatusTranslationKey,
  buildTherapyTypeTranslationKey,
} from '@/constants/appointmentOptions';
import { resolveAppointmentColorClasses } from '@/constants/appointmentColors';
import {
  formatDateToDayMonthYear,
  formatIsoDateAndTimeToHistoryLabel,
} from '@/utils/dateFormatting';
import type { PatientTimelineEvent } from '@/api/patients/patientsApi.types';
import { groupTimelineEventsByMonth } from './groupTimelineEventsByMonth';

interface CaseTimelineProps {
  patientId: string;
}

export function CaseTimeline({ patientId }: CaseTimelineProps) {
  const { t } = useTranslation(['patients', 'appointments', 'clients']);
  const navigate = useNavigate();
  const timelineQuery = usePatientTimelineQuery(patientId);

  function openEvent(event: PatientTimelineEvent) {
    if (event.kind === 'session_note') {
      navigate(
        generatePath(APPLICATION_ROUTES.workspacePatientSessionNote, {
          patientId,
          noteId: event.id,
        })
      );
      return;
    }
    if (event.kind === 'first_consultation') {
      navigate(
        generatePath(APPLICATION_ROUTES.workspacePatientFirstConsultation, {
          patientId,
          consultationId: event.id,
        })
      );
      return;
    }
    if (event.kind === 'treatment_plan') {
      navigate(
        generatePath(APPLICATION_ROUTES.workspacePatientTreatmentPlan, {
          patientId,
          planId: event.id,
        })
      );
    }
  }

  const events =
    timelineQuery.data === undefined
      ? []
      : timelineQuery.data.pages.flatMap(function collectPageItems(page) {
          return page.items;
        });
  const monthGroups = groupTimelineEventsByMonth(events);
  const hasNextPage = timelineQuery.hasNextPage === true;

  return (
    <RelatedRecordsSection
      title={t('patients:timeline.title')}
      isLoading={timelineQuery.isLoading}
      isError={timelineQuery.isError}
      onRetry={function retry() {
        timelineQuery.refetch();
      }}
    >
      {events.length === 0 ? (
        <p className="text-sm text-zoomuted py-4">{t('patients:timeline.empty')}</p>
      ) : (
        <div>
          <div className="md:max-h-[32rem] md:overflow-y-auto md:pr-1">
            {monthGroups.map(function renderMonthGroup(group) {
              return (
                <div key={group.monthLabel} className="mb-4 last:mb-0">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-zoomuted mb-2">
                    {group.monthLabel}
                  </h3>
                  <ol className="space-y-3">
                    {group.events.map(function renderEvent(event) {
                      return (
                        <TimelineRow
                          key={event.kind + '-' + event.id}
                          event={event}
                          onOpenEvent={openEvent}
                        />
                      );
                    })}
                  </ol>
                </div>
              );
            })}
          </div>
          {hasNextPage ? (
            <div className="pt-3 flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="small"
                isLoading={timelineQuery.isFetchingNextPage}
                onClick={function loadOlder() {
                  timelineQuery.fetchNextPage();
                }}
              >
                {t('patients:timeline.showOlder')}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </RelatedRecordsSection>
  );
}

interface TimelineRowProps {
  event: PatientTimelineEvent;
  onOpenEvent: (event: PatientTimelineEvent) => void;
}

function TimelineRow({ event, onOpenEvent }: TimelineRowProps) {
  const { t } = useTranslation(['patients', 'appointments', 'clients']);
  const details = resolveEventDetails(event, t);
  const isOpenable =
    event.kind === 'session_note' ||
    event.kind === 'first_consultation' ||
    event.kind === 'treatment_plan';
  const isDraftSessionNote = event.kind === 'session_note' && event.status !== 'completed';
  const dateLabel =
    event.eventTime !== null
      ? formatIsoDateAndTimeToHistoryLabel(event.eventDate, event.eventTime)
      : formatDateToDayMonthYear(parseISO(event.eventDate));

  return (
    <li
      className={
        'flex gap-3 rounded-lg' +
        (isDraftSessionNote ? ' border-l-4 border-l-zooerrortext pl-2' : '')
      }
    >
      <div className="flex flex-col items-center">
        <span className="w-8 h-8 rounded-full bg-zoovpalelavender flex items-center justify-center text-zooprimary shrink-0">
          <Icon name={details.icon} />
        </span>
        <span className="flex-1 w-px bg-zooborder mt-1" />
      </div>
      <div className="flex-1 min-w-0 pb-1">
        <p className="text-xs text-zoomuted">{dateLabel}</p>
        {isOpenable ? (
          <button
            type="button"
            onClick={function open() {
              onOpenEvent(event);
            }}
            className="text-sm font-semibold text-zooprimary hover:underline text-left mt-0.5"
          >
            {details.title}
          </button>
        ) : (
          <p className="text-sm font-semibold text-zooindigo mt-0.5">{details.title}</p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          {isDraftSessionNote ? (
            <Badge tone="danger" size="sm">
              {t('patients:sessionNote.statusDraft')}
            </Badge>
          ) : null}
          {details.statusLabel === null ? null : (
            <span
              className={
                'inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ' +
                details.statusClassName
              }
            >
              {details.statusLabel}
            </span>
          )}
        </div>
        {details.note === null ? null : (
          <p className="text-xs text-zoomuted mt-1 whitespace-pre-wrap">{details.note}</p>
        )}
      </div>
    </li>
  );
}

interface EventDetails {
  icon: IconName;
  title: string;
  statusLabel: string | null;
  statusClassName: string;
  note: string | null;
}

function resolveEventDetails(
  event: PatientTimelineEvent,
  t: (key: string, options?: Record<string, string>) => string
): EventDetails {
  if (event.kind === 'appointment') {
    const therapyType = event.therapyType === null ? 'other' : event.therapyType;
    const status = event.status === null ? 'planned' : event.status;
    const colorClasses = resolveAppointmentColorClasses(therapyType);
    return {
      icon: 'Calendar',
      title: t(buildTherapyTypeTranslationKey(therapyType)),
      statusLabel: t(buildAppointmentStatusTranslationKey(status)),
      statusClassName: colorClasses.softBg + ' ' + colorClasses.accentText,
      note: event.note,
    };
  }
  if (event.kind === 'session_note') {
    return {
      icon: 'FileText',
      title: t('patients:timeline.sessionNote'),
      statusLabel: null,
      statusClassName: '',
      note: event.note,
    };
  }
  if (event.kind === 'first_consultation') {
    return {
      icon: 'ClipboardCheck',
      title: t('patients:timeline.firstConsultation'),
      statusLabel: null,
      statusClassName: '',
      note: event.note,
    };
  }
  if (event.kind === 'treatment_plan') {
    return {
      icon: 'Clipboard',
      title: t('patients:timeline.treatmentPlan'),
      statusLabel: null,
      statusClassName: '',
      note: event.note,
    };
  }
  if (event.kind === 'file') {
    const visibilityLabel =
      event.fileVisibility === 'client_visible'
        ? t('patients:timeline.fileClientVisible')
        : t('patients:timeline.fileTherapistOnly');
    return {
      icon: 'FileText',
      title: event.fileName === null ? t('patients:timeline.file') : event.fileName,
      statusLabel: visibilityLabel,
      statusClassName: 'bg-zoovpalelavender text-zooprimary',
      note: null,
    };
  }
  if (event.kind === 'message') {
    const messageType = event.messageType === null ? 'message' : event.messageType;
    const messageChannel = event.messageChannel === null ? 'email' : event.messageChannel;
    return {
      icon: 'Mail',
      title: t('patients:timeline.messageType.' + messageType),
      statusLabel: t('patients:timeline.messageChannel.' + messageChannel),
      statusClassName: 'bg-zoovpalelavender text-zooprimary',
      note: null,
    };
  }
  if (event.kind === 'consent') {
    const consentType = event.consentType === null ? '' : event.consentType;
    const decisionLabel =
      event.consentDecision === CONSENT_DECISION.agreed
        ? t('patients:timeline.decisionAgreed')
        : event.consentDecision === CONSENT_DECISION.withdrawn
          ? t('patients:timeline.decisionWithdrawn')
          : t('patients:timeline.decisionDeclined');
    return {
      icon: 'ShieldCheck',
      title: t('patients:timeline.consent', { type: t('clients:consents.type.' + consentType) }),
      statusLabel: decisionLabel,
      statusClassName: 'bg-zoovpalelavender text-zooprimary',
      note: null,
    };
  }
  return {
    icon: 'PawPrint',
    title: t('patients:timeline.recordCreated'),
    statusLabel: null,
    statusClassName: '',
    note: null,
  };
}
